import React, { useEffect, useState } from 'react';
import {
  Copy, KeyRound, Loader2, RefreshCcw, RefreshCw, Save, Send, TestTube2,
} from 'lucide-react';
import client from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import ConfirmDialog from './ConfirmDialog';
import {
  CALLBACK_PATH, CHECKOUT_SLUG, CRYPTOBOX_DEFAULTS, CRYPTOBOX_METHODS,
  RATE_MODES, REDIRECT_PATH, changedFields, hasMethod, toggleMethod,
} from '../lib/cryptobox';

const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object') return detail.message || fallback;
  return detail || fallback;
};

/**
 * Ayarlar → Finans → CryptoBox.
 *
 * Bölüm kendi state'ini ve Kaydet düğmesini taşır: ayarlar
 * `provider_configs` satırındadır ve `PATCH /admin/settings` gövdesine hiç
 * girmez. `onEnabledChange` finans bölüm seçicisindeki aktiflik noktasını
 * besler.
 */
const CryptoBoxSettings = ({ paymentBase = '', onEnabledChange }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(CRYPTOBOX_DEFAULTS);
  const [initial, setInitial] = useState(CRYPTOBOX_DEFAULTS);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);
  const [confirmHmac, setConfirmHmac] = useState(false);
  const [mockChain, setMockChain] = useState('BTC');
  const [lastEvent, setLastEvent] = useState(null);
  // Vitrin durumu ayar gövdesinden ayrı tutulur: oyuncunun listesinde kaç
  // CryptoBox yöntemi olduğunu ve yoksa sebebini yalnızca bu alan söyler.
  const [showcase, setShowcase] = useState(null);
  // Ayarlar okunamadıysa form **kayıtlı değerleri göstermez**, varsayılanları
  // çizer. Kaydetmeye izin vermek o varsayılanları canlının üzerine yazmak
  // olurdu (canlıda min yatırım 1.000 ₺ iken ekranda 100 görünüyordu), bu
  // yüzden bölüm okunana kadar salt okunurdur.
  const [loadError, setLoadError] = useState('');

  const loadStatus = () => client.get('/admin/cryptobox/status')
    .then(({ data }) => setLastEvent(data?.last_event || null))
    .catch(() => {});

  const callbackUrl = `${paymentBase || window.location.origin}${CALLBACK_PATH}`;
  const defaultRedirect = `${paymentBase || window.location.origin}${REDIRECT_PATH}`;

  const apply = (data) => {
    const merged = {
      ...CRYPTOBOX_DEFAULTS,
      ...data,
      api_key: '',
      hmac_secret: '',
    };
    setForm(merged);
    setInitial(merged);
    setMeta(data || {});
    if (data?.showcase) setShowcase(data.showcase);
    onEnabledChange?.(Boolean(data?.enabled));
  };

  const loadConfig = (alive = { current: true }) => {
    setLoading(true);
    return client.get('/admin/cryptobox/config')
      .then(({ data }) => {
        if (!alive.current) return;
        apply(data);
        setLoadError('');
      })
      .catch((error) => {
        if (!alive.current) return;
        const message = errorText(error, 'Bilinmeyen hata');
        setLoadError(message);
        toast({
          title: 'CryptoBox ayarları okunamadı',
          description: message,
          variant: 'destructive',
        });
      })
      .finally(() => { if (alive.current) setLoading(false); });
  };

  useEffect(() => {
    const alive = { current: true };
    loadConfig(alive);
    loadStatus();
    return () => { alive.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const pending = changedFields(form, initial);
  const dirty = Object.keys(pending).length > 0;

  const save = async () => {
    if (loadError) return;
    const payload = { ...pending };
    // Dönüş adresi boşsa sitenin kendi yatırım sayfası yazılır.
    if (!form.redirect_url && !initial.redirect_url) {
      payload.redirect_url = defaultRedirect;
    }
    if (!Object.keys(payload).length) return;
    setBusy('save');
    try {
      const { data } = await client.patch('/admin/cryptobox/config', payload);
      apply(data);
      toast({ title: 'CryptoBox ayarları kaydedildi' });
    } catch (error) {
      toast({
        title: 'CryptoBox kaydedilemedi',
        description: errorText(error, 'Bilinmeyen hata'),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const run = async (key, request, successTitle) => {
    if (loadError) return null;
    setBusy(key);
    setResult(null);
    try {
      if (dirty) {
        const { data } = await client.patch('/admin/cryptobox/config', pending);
        apply(data);
      }
      const { data } = await request();
      setResult({ ok: true, key, data });
      toast({ title: successTitle });
      return data;
    } catch (error) {
      const message = errorText(error, 'Bilinmeyen hata');
      setResult({ ok: false, key, message });
      toast({ title: 'CryptoBox isteği başarısız', description: message, variant: 'destructive' });
      return null;
    } finally {
      setBusy('');
    }
  };

  const sendMock = async () => {
    const data = await run(
      'mock',
      () => client.post('/admin/cryptobox/mock-deposit', { chain: mockChain }),
      'Test bildirimi istendi',
    );
    // Bildirim sağlayıcının kuyruğundan gelir; birkaç saniye sonra bakılır.
    if (data) setTimeout(loadStatus, 5000);
  };

  // Kur ucu iş reddini **HTTP 200** ile bildirir (kaynak cevap vermedi, sapma
  // reddedildi): `run` yalnızca ağ hatasını yakalar, sonucu burada ayırmak
  // gerekir — aksi hâlde güncellenmemiş kur "güncellendi" diye görünürdü.
  const refreshRate = async () => {
    const data = await run(
      'rate',
      () => client.post('/admin/cryptobox/rate/refresh'),
      'Kur kaynağı soruldu',
    );
    if (!data) return;
    if (data.config) apply(data.config);
    if (data.ok) {
      toast({
        title: `Kur güncellendi: ${Number(data.rate).toLocaleString('tr-TR')} ₺`,
        description: `Kaynak: ${data.source}`,
      });
    } else {
      setResult({ ok: false, key: 'rate', message: data.error || 'Kur güncellenemedi.' });
      toast({
        title: 'Kur güncellenmedi',
        description: data.error || 'Kaynaklar cevap vermedi; son kur korundu.',
        variant: 'destructive',
      });
    }
  };

  const enableHmac = async () => {
    setConfirmHmac(false);
    const data = await run(
      'hmac',
      () => client.post('/admin/cryptobox/hmac', { confirm: true }),
      'HMAC güvenliği etkinleştirildi',
    );
    if (data?.config) apply(data.config);
  };

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: 'Kopyalandı' });
    } catch {
      toast({ title: 'Kopyalanamadı', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="col-span-full flex items-center gap-2 text-sm text-slate-400">
        <Loader2 size={14} className="animate-spin" /> CryptoBox ayarları yükleniyor
      </div>
    );
  }

  const age = meta.rate_age_hours;
  const rateAgeLabel = age == null
    ? 'henüz okunmadı'
    : age < 1
      ? 'son güncelleme: 1 saatten az önce'
      : `son güncelleme: ${Math.round(age)} saat önce`;
  const sourceNote = (meta.rate_sources || []).find(
    (item) => item.key === form.rate_source)?.note;
  const assetPriceCount = Object.keys(meta.asset_prices || {}).length;

  return (
    <>
      <div className="col-span-full mt-1">
        <h3 className="text-sm font-bold text-white">CryptoBox</h3>
        <p className="mt-1 text-xs text-slate-400">
          Kripto yatırım ve çekim. Yatırım bir talep değil <strong>adrestir</strong>:
          {' '}sağlayıcı her üyeye kalıcı adresler üretir ve gelen her ödemeyi
          bildirir — oyuncu ödeme ekranını açmadan gönderse bile para kredilenir.
          Sağlayıcı <strong>TRY tanımaz</strong>; bakiyeye yazılan tutar USD
          karşılığı × aşağıdaki kurdur, çekim de aynı kurla USD'ye çevrilir.
          Çekim önce <strong>yönetici onayından</strong>, sonra sağlayıcıda
          iki kişilik onaydan geçer.
        </p>
      </div>

      {loadError && (
        <div className="col-span-full rounded-lg border border-rose-600/60 bg-rose-950/20 p-3 text-xs text-rose-200">
          <strong>CryptoBox ayarları okunamadı, bölüm salt okunur.</strong>
          <span className="mt-1 block text-[11px] text-rose-300/90">
            Aşağıda gördükleriniz <strong>kayıtlı ayarlarınız değil</strong>,
            varsayılan değerlerdir. Kayıtlı ayarlara bir şey olmadı; üzerlerine
            yanlışlıkla varsayılan yazılmasın diye kaydetme kapatıldı.
          </span>
          <span className="mt-1 block text-[11px] text-rose-300/70">
            Sebep: {loadError}
          </span>
          <button
            type="button"
            onClick={() => loadConfig()}
            disabled={loading}
            className="admin-btn-secondary mt-2 inline-flex items-center gap-2 px-3 py-1.5"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : null}
            Yeniden Dene
          </button>
        </div>
      )}

      {!loadError && showcase && (
        <div className={`col-span-full rounded-lg border p-3 text-xs ${
          showcase.live_methods > 0
            ? 'border-emerald-700/50 text-slate-300'
            : 'border-amber-600/60 text-amber-200'
        }`}>
          {showcase.live_methods > 0 ? (
            <>
              <strong className="text-emerald-400">
                Oyuncunun yatırım ekranında {showcase.live_methods} CryptoBox
                yöntemi görünüyor
              </strong>
              {showcase.hosted_open
                ? ' — sağlayıcının ödeme sayfası da listede ("Kripto Ödeme Sayfası").'
                : ' — sağlayıcının ödeme sayfası listede değil, yalnızca adresler.'}
              {showcase.method_names?.length > 0 && (
                <span className="mt-1 block text-[11px] text-slate-400">
                  {showcase.method_names.join(' · ')}
                </span>
              )}
            </>
          ) : (
            <>
              <strong>Oyuncunun yatırım ekranında hiçbir CryptoBox yöntemi
              görünmüyor.</strong>
              {showcase.blockers?.length > 0 ? (
                <span className="mt-1 block">
                  Eksik: {showcase.blockers.join(' · ')}
                </span>
              ) : (
                <span className="mt-1 block">
                  Ayarlar tamam görünüyor; kaydedip bu satırı tekrar kontrol
                  edin.
                </span>
              )}
            </>
          )}
        </div>
      )}

      <div className="col-span-full provider-enable">
        <label className="flex items-center gap-2 text-sm font-semibold text-white">
          <input
            type="checkbox"
            checked={!!form.enabled}
            onChange={(e) => set('enabled', e.target.checked)}
          />
          CryptoBox entegrasyonu aktif
        </label>
        <p className="mt-1 text-[11px] text-slate-400">
          Açmak için API anahtarı, etkin HMAC, USD/TRY kuru ve bildirim adresi
          onayı gerekir.
        </p>
      </div>

      <label className="settings-inline-field">
        API adresi
        <input
          value={form.api_url || ''}
          onChange={(e) => set('api_url', e.target.value)}
          className="admin-input mt-1"
        />
      </label>
      <label className="settings-inline-field">
        API anahtarı (x-api-key)
        <input
          type="password"
          autoComplete="new-password"
          placeholder={meta.api_key_configured ? 'Kayıtlı — değiştirmek için yazın' : 'API anahtarı'}
          value={form.api_key}
          onChange={(e) => set('api_key', e.target.value)}
          className="admin-input mt-1"
        />
      </label>

      <div className="col-span-full rounded-lg border border-slate-700/60 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-white">
            HMAC güvenliği:{' '}
            {meta.hmac_configured
              ? <span className="font-semibold text-emerald-400">etkin</span>
              : <span className="font-semibold text-amber-400">etkin değil</span>}
            {meta.previous_hmac_active && (
              <span className="ml-2 text-[11px] text-slate-400">
                (önceki secret 48 saat daha kabul ediliyor)
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setConfirmHmac(true)}
            disabled={!!busy || !(meta.api_key_configured || form.api_key)}
            className="admin-btn-secondary inline-flex items-center gap-2 px-3 py-2 text-xs"
          >
            {busy === 'hmac' ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />}
            {meta.hmac_configured ? "HMAC'i Yenile" : "HMAC'i Etkinleştir"}
          </button>
        </div>
        <p className="mt-2 text-[11px] leading-4 text-slate-400">
          Secret'ı biz üretiriz ve sağlayıcıya bir kez bildiririz; her istek ve
          her bildirim onunla imzalanır. Sağlayıcıda zaten tanımlı bir secret
          varsa aşağıya yazın.
        </p>
        <input
          type="password"
          autoComplete="new-password"
          placeholder="Mevcut HMAC secret (isteğe bağlı, 16-64 karakter)"
          value={form.hmac_secret}
          onChange={(e) => set('hmac_secret', e.target.value)}
          className="admin-input mt-2"
        />
      </div>

      <div className="col-span-full rounded-lg border border-slate-700/60 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-white">
            USD/TRY kuru:{' '}
            <strong className={meta.rate_stale ? 'text-amber-400' : 'text-emerald-400'}>
              {Number(form.usd_try_rate) > 0
                ? Number(form.usd_try_rate).toLocaleString('tr-TR', {
                  minimumFractionDigits: 2, maximumFractionDigits: 4,
                })
                : 'okunmadı'}
            </strong>
            <span className="ml-2 text-[11px] text-slate-400">
              {rateAgeLabel}
              {meta.rate_source_used && meta.rate_source_used !== form.rate_source
                && ` · yedek kaynak: ${meta.rate_source_used}`}
            </span>
          </div>
          <button
            type="button"
            onClick={refreshRate}
            disabled={!!busy}
            className="admin-btn-secondary inline-flex items-center gap-2 px-3 py-2 text-xs"
          >
            {busy === 'rate' ? <Loader2 size={13} className="animate-spin" /> : <RefreshCcw size={13} />}
            Kuru Şimdi Güncelle
          </button>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="settings-inline-field">
            Kur kaynağı
            <select
              value={form.rate_mode || 'auto'}
              onChange={(e) => set('rate_mode', e.target.value)}
              className="admin-input mt-1"
            >
              {RATE_MODES.map((mode) => (
                <option key={mode.key} value={mode.key}>{mode.label}</option>
              ))}
            </select>
          </label>
          {form.rate_mode !== 'manual' ? (
            <label className="settings-inline-field">
              Veri kaynağı
              <select
                value={form.rate_source || 'binance'}
                onChange={(e) => set('rate_source', e.target.value)}
                className="admin-input mt-1"
              >
                {(meta.rate_sources || []).map((item) => (
                  <option key={item.key} value={item.key}>{item.label}</option>
                ))}
              </select>
            </label>
          ) : (
            <label className="settings-inline-field">
              USD/TRY kuru (1 USD = ? ₺)
              <input
                type="number"
                min="0"
                step="0.0001"
                value={form.usd_try_rate || ''}
                onChange={(e) => set('usd_try_rate', e.target.value)}
                className="admin-input mt-1"
              />
            </label>
          )}
        </div>
        <p className="mt-2 text-[11px] leading-4 text-slate-400">
          {sourceNote || (
            "Yatırımda USD karşılığı bu kurla TL'ye çevrilip bakiyeye yazılır; "
            + "çekimde TL tutar bu kurla USD'ye çevrilip gönderilir."
          )}
          {form.rate_mode !== 'manual' && (
            <>
              {' '}Kur her {meta.rate_refresh_minutes || 15} dakikada bir
              kendiliğinden okunur; kaynak cevap vermezse son bilinen kur
              kullanılmaya devam eder ve ani sapmalar (%25+) yazılmaz.
            </>
          )}
        </p>
        {/* Coin fiyatı kurla aynı turda okunur ve oyuncuya "şu kadar BTC
            gönderin" diyebilmenin şartıdır; eksikse ekranda yalnızca TL
            tutarı görünür ve oyuncu ne göndereceğini bilemez. */}
        <p className="mt-2 text-[11px] leading-4 text-slate-400">
          Coin fiyatları:{' '}
          {assetPriceCount > 0 ? (
            <strong className="text-emerald-400">
              {assetPriceCount} coin okundu
              {meta.asset_prices_source && ` (${meta.asset_prices_source})`}
            </strong>
          ) : (
            <strong className="text-amber-400">okunmadı</strong>
          )}
          {' '}— ödeme ekranında gönderilecek coin miktarı bu fiyatlardan
          hesaplanır.
        </p>
        {meta.rate_error && (
          <p className="mt-2 text-[11px] leading-4 text-amber-300">
            Son kur denemesi: {meta.rate_error}
          </p>
        )}
      </div>
      <label className="settings-inline-field">
        Ödeme sayfası dili
        <select
          value={form.session_language || 'en'}
          onChange={(e) => set('session_language', e.target.value)}
          className="admin-input mt-1"
        >
          <option value="en">İngilizce (en)</option>
          <option value="tr">Türkçe (tr)</option>
        </select>
      </label>

      <div className="col-span-full rounded-lg border border-slate-700/60 p-3">
        <div className="text-xs text-slate-300">Bildirim adresi (yatırım ve çekim için aynı)</div>
        <div className="mt-1 flex items-center gap-2">
          <code className="flex-1 break-all rounded bg-slate-900/60 px-2 py-1.5 text-xs text-slate-200">
            {callbackUrl}
          </code>
          <button type="button" onClick={() => copy(callbackUrl)} className="admin-btn-secondary p-2" aria-label="Kopyala">
            <Copy size={13} />
          </button>
        </div>
        <p className="mt-2 text-[11px] leading-4 text-slate-400">
          Adres istekte gönderilmez; <strong>sağlayıcının yöneticileri</strong>
          {' '}tanımlar. Sunucunun çıkış IPv4 adresini de beyaz listelerine
          ekletin. Bildirimler şu adreslerden gelir:{' '}
          {(meta.callback_source_ips || []).join(', ')}.
        </p>
        <label className="mt-2 flex items-center gap-2 text-xs text-white">
          <input
            type="checkbox"
            checked={!!form.callback_registered}
            onChange={(e) => set('callback_registered', e.target.checked)}
          />
          Bildirim adresini sağlayıcıya tanımlattım
        </label>
      </div>

      <label className="settings-inline-field col-span-full">
        Ödeme sonrası dönüş adresi
        <input
          value={form.redirect_url || ''}
          placeholder={defaultRedirect}
          onChange={(e) => set('redirect_url', e.target.value)}
          className="admin-input mt-1"
        />
      </label>

      {[
        { key: 'deposit', title: 'yatırım', cls: 'is-deposit', field: 'deposit_methods' },
        { key: 'withdraw', title: 'çekim', cls: 'is-withdraw', field: 'withdraw_methods' },
      ].map((side) => (
        <section key={side.key} className={`provider-money ${side.cls}`}>
          <label className="flex items-center gap-2 text-sm font-bold text-white">
            <input
              type="checkbox"
              checked={form[`${side.key}_enabled`] !== false}
              onChange={(e) => set(`${side.key}_enabled`, e.target.checked)}
            />
            CryptoBox {side.title} açık
          </label>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {['min', 'max'].map((bound) => (
              <label key={bound} className="settings-inline-field">
                {bound === 'min' ? 'Minimum' : 'Maksimum'} {side.title} (₺)
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form[`${side.key}_${bound}_amount`] ?? ''}
                  onChange={(e) => set(`${side.key}_${bound}_amount`, Number(e.target.value))}
                  className="admin-input mt-1"
                />
              </label>
            ))}
          </div>
          {/* Hosted sayfa diğer dokuz coinle aynı hizada sıradan bir kutuydu
              ve yöneticiye ne olduğunu söylemiyordu: "ödeme sayfası mı, bizim
              ekranımız mı" seçimi tam da burada yapılıyor. */}
          {side.key === 'deposit' && (
            <label className="mt-3 flex items-start gap-2 rounded-lg border border-slate-700/60 p-3 text-xs text-slate-300">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={hasMethod(form.deposit_methods, CHECKOUT_SLUG)}
                onChange={(e) => set(
                  'deposit_methods',
                  toggleMethod(form.deposit_methods, CHECKOUT_SLUG, e.target.checked))}
              />
              <span>
                <strong className="text-white">Sağlayıcının ödeme sayfası</strong>
                {' '}— oyuncu siteden çıkar, kripto parayı ve ağı CryptoBox'ın
                kendi sayfasında seçer, tutarı orada görür. Yatırım ekranında
                tek bir yöntem satırı olarak görünür.
              </span>
            </label>
          )}
          {side.key === 'deposit' && (
            <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Sitede gösterilen adresler — oyuncu siteden çıkmaz
            </p>
          )}
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {CRYPTOBOX_METHODS
              .filter((m) => (side.key === 'deposit' ? m.deposit : m.withdraw))
              .filter((m) => !(side.key === 'deposit' && m.slug === CHECKOUT_SLUG))
              .map((m) => (
                <label key={`cb-${side.key}-${m.slug}`} className="flex items-center gap-2 text-xs text-slate-300">
                  <input
                    type="checkbox"
                    checked={hasMethod(form[side.field], m.slug)}
                    onChange={(e) => set(side.field, toggleMethod(form[side.field], m.slug, e.target.checked))}
                  />
                  {m.label}
                </label>
              ))}
          </div>
          {side.key === 'deposit' && (
            <p className="mt-2 text-[11px] leading-4 text-slate-400">
              Her satır oyuncunun yatırım ekranında ayrı bir yöntemdir ve
              adresi bizim sayfamızda gösterilir; gönderilecek coin miktarı
              yukarıdaki kur ve coin fiyatlarından hesaplanır. İkisi birlikte
              açık kalabilir — oyuncu hangisini seçeceğine kendi karar verir.
            </p>
          )}
          {side.key === 'deposit' && !String(form.deposit_methods || '').trim() && (
            <p className="mt-2 text-[11px] leading-4 text-amber-300">
              Hiçbir yatırım yöntemi seçili değil: CryptoBox yatırım ekranında
              hiç görünmez.
            </p>
          )}
          {side.key === 'withdraw' && (
            <p className="mt-2 text-[11px] leading-4 text-slate-400">
              XRP çekimi açılamaz: çekim formunda hedef etiketi alanı yoktur ve
              etiketsiz gönderim geri alınamaz.
            </p>
          )}
        </section>
      ))}

      <div className="col-span-full flex flex-wrap gap-2">
        <button
          type="button"
          onClick={save}
          disabled={!!busy || !!loadError || (!dirty && (form.redirect_url || initial.redirect_url))}
          className="admin-btn-primary inline-flex items-center gap-2 px-4 py-2.5"
        >
          {busy === 'save' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          CryptoBox Ayarlarını Kaydet
        </button>
        <button
          type="button"
          onClick={() => run('test', () => client.post('/admin/cryptobox/test'), 'CryptoBox bağlantısı doğrulandı')}
          disabled={!!busy}
          className="admin-btn-secondary inline-flex items-center gap-2 px-4 py-2.5"
        >
          {busy === 'test' ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
          Bağlantıyı Test Et
        </button>
        <span className="inline-flex items-center gap-2">
          <select
            value={mockChain}
            onChange={(e) => setMockChain(e.target.value)}
            className="admin-input admin-input-w-sm"
          >
            {CRYPTOBOX_METHODS.filter((m) => m.slug !== 'checkout').map((m) => (
              <option key={`mock-${m.slug}`} value={m.slug}>{m.slug}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={sendMock}
            disabled={!!busy || !meta.hmac_configured}
            className="admin-btn-secondary inline-flex items-center gap-2 px-4 py-2.5"
          >
            {busy === 'mock' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Test Bildirimi Gönder
          </button>
        </span>
        <button
          type="button"
          onClick={() => run('reconcile', () => client.post('/admin/cryptobox/reconcile'), 'Bekleyen işlemler sorgulandı')}
          disabled={!!busy || !form.enabled}
          className="admin-btn-secondary inline-flex items-center gap-2 px-4 py-2.5"
        >
          {busy === 'reconcile' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Bekleyenleri Sorgula
        </button>
      </div>
      <p className="col-span-full text-[11px] leading-4 text-slate-400">
        <strong>Test Bildirimi</strong> sağlayıcıdan sizin üye kimliğinizle
        sıfır tutarlı bir yatırım bildirimi ister: HMAC secret'ı ve bildirim
        adresini birlikte kanıtlar, bakiyeye dokunmaz. Bildirimin ulaşıp
        ulaşmadığını aşağıdaki son bildirim satırı söyler.
      </p>
      <div className="col-span-full flex items-center gap-2 text-xs text-slate-300">
        Son bildirim:{' '}
        {lastEvent
          ? `${lastEvent.type === 'withdraw' ? 'çekim' : 'yatırım'} · ${lastEvent.status} · ${String(lastEvent.created_at || '').replace('T', ' ').slice(0, 19)} (UTC)`
          : 'henüz yok'}
        <button type="button" onClick={loadStatus} className="admin-btn-secondary p-1.5" aria-label="Yenile">
          <RefreshCw size={12} />
        </button>
      </div>

      {result && (
        <div
          className={`col-span-full rounded-lg border p-3 text-xs ${
            result.ok ? 'border-emerald-500/40 text-emerald-200' : 'border-rose-500/40 text-rose-200'
          }`}
        >
          {result.ok
            ? (result.data?.message || (result.key === 'mock'
              ? (result.data?.result?.mockDepositResult?.detailedLogs || []).join(' · ') || 'İstek gönderildi.'
              : result.key === 'reconcile'
                ? `${result.data?.processed ?? 0} kayıt sorgulandı.`
                : 'Tamam.'))
            : result.message}
        </div>
      )}

      <ConfirmDialog
        open={confirmHmac}
        title={meta.hmac_configured ? 'HMAC secret yenilensin mi?' : 'HMAC güvenliği etkinleştirilsin mi?'}
        description={
          'Yeni bir secret üretilip sağlayıcıya bildirilecek. Sağlayıcı bunu '
          + 'yalnızca trafik yokken yapmanızı ister: o an süren bir istek eski '
          + 'imzayla reddedilebilir. Önceki secret, yoldaki bildirimler için '
          + '48 saat daha kabul edilir.'
        }
        confirmLabel="Devam et"
        tone="warning"
        busy={busy === 'hmac'}
        onCancel={() => setConfirmHmac(false)}
        onConfirm={enableHmac}
      />
    </>
  );
};

export default CryptoBoxSettings;
